import DataLoader from 'dataloader'
import { entries, fromPairs, groupBy, uniqBy } from 'lodash-es'

import { ConversionRatesRequest, ConversionRatesResponse } from './api/CurrencyConversionAPITypes'
import { CurrencyConversionRatesQuery } from './domain/ConversionRates'

export const createCurrencyConversionRatesDataLoader = (
  getConversionRates: (request: ConversionRatesRequest) => Promise<ConversionRatesResponse>
): DataLoader<CurrencyConversionRatesQuery, number, string> =>
  new DataLoader(async (queries: readonly CurrencyConversionRatesQuery[]) => {
    // DataLoader doesn't create distinct queries unless we provide cacheKeyFn and set cache=true
    // But we are using cache from react-query, so we need to turn off dataloader caching
    // And hence, we need to create distinct queries ourselves
    const distinctQueries = uniqBy(
      queries,
      (query) => `${query.currencyExchangeRateId}-${query.fromCurrencyCode}-${query.toCurrencyCode}`
    )
    // convert queries to conversionRateRequests with batched pairs
    const groupedQueries = groupBy(distinctQueries, 'currencyExchangeRateId')
    const conversionRatesRequests = entries(groupedQueries).map(
      ([currencyExchangeRateId, queriesForCurrencyExchangeRateId]) => ({
        currencyExchangeRateId,
        pairs: queriesForCurrencyExchangeRateId.map((query) => ({
          from: query.fromCurrencyCode,
          to: query.toCurrencyCode,
        })),
      })
    )

    const responses = await Promise.all(conversionRatesRequests.map((request) => getConversionRates(request)))

    // order responses by exchange rate id
    const responsesByCurrencyExchangeRateId = fromPairs(
      responses.map((response) => [response.transactionId, response.rates])
    )

    return queries.map(
      (query) =>
        responsesByCurrencyExchangeRateId[query.currencyExchangeRateId]![query.fromCurrencyCode]![query.toCurrencyCode]!
    )
  })
